import { logger } from "@/lib/logger";
import { handleApiResponse } from "@/lib/utils/api-response-handler";
import { authApiClient } from "./client";

// 登录请求参数（邮箱密码）
export interface LoginRequest {
  clientId: string;
  authType: "EMAIL_PASSWORD";
  email: string;
  password: string;
}

// Google OAuth 回调登录请求参数（POST /auth/login）
export interface GoogleOAuthCallbackLoginRequest {
  clientId: string;
  authType: "SOCIAL";
  source: "google";
  code: string;
  state: string;
  /** 注册邀请码（可选，首次三方登录注册时使用） */
  inviteCode?: string;
}

// 登录响应
export interface LoginResponse {
  code: number;
  msg: string;
  success: boolean;
  timestamp: number;
  data: {
    token: string;
  };
}

// 注册请求参数
export interface RegisterRequest {
  email: string;
  password: string;
  captcha: string;
  /** 注册邀请码（可选，支持用户邀请码和渠道邀请码） */
  inviteCode?: string;
}

export type RegisterAttributionType = "USER_REFERRAL" | "CHANNEL" | "DEFAULT";

export type RegisterRewardStatus = "SUCCESS" | "INVITER_LIMIT_REACHED" | "DEFAULT_GRANTED";

export interface RegisterResult {
  success: boolean;
  attributionType: RegisterAttributionType;
  rewardStatus: RegisterRewardStatus;
  inviteCode: string | null;
  grantedCredits: number;
  defaultWelcomeCredits: number;
  messageKey: string;
}

// 注册响应
export interface RegisterResponse {
  code: number;
  msg: string;
  success: boolean;
  timestamp: number;
  data: RegisterResult | null;
}

export type RegisterInviteType = "USER_REFERRAL" | "CHANNEL";

export type RegisterInviteReasonCode =
  | "INVITE_CODE_BLANK"
  | "INVITE_CODE_UNAVAILABLE"
  | "INVITER_LIMIT_REACHED";

export interface RegisterInviteCodeValidateResult {
  inviteCode: string;
  valid: boolean;
  inviteType: RegisterInviteType | null;
  reasonCode: RegisterInviteReasonCode | null;
  limitReached: boolean;
  shouldClearInviteCode: boolean;
  canRegisterWithoutInvite: boolean;
  registerRewardCredits: number;
  inviterRewardCredits: number;
  inviteeRewardCredits: number;
  defaultWelcomeCredits: number;
}

export interface RegisterInviteCodeValidateResponse {
  code: number;
  msg: string;
  success: boolean;
  timestamp?: number;
  data: RegisterInviteCodeValidateResult | null;
}

// 用户信息响应
export interface UserInfo {
  id: number;
  username: string;
  nickname: string;
  gender: number;
  email: string;
  phone: string;
  avatar: string;
  description: string;
  pwdResetTime: string;
  pwdExpired: boolean;
  registrationDate: string;
  deptId: number;
  deptName: string;
  permissions: string[];
  roles: string[];
  roleNames: string;
}

export interface UserInfoResponse {
  code: number;
  msg: string;
  success: boolean;
  timestamp: number;
  data: UserInfo;
}

export interface UpdateUserProfileRequest {
  nickname?: string;
  avatar?: File;
}

export interface UpdateUserProfileResult {
  nickname: string;
  avatar: string;
}

// 发送验证码请求参数
export interface SendCaptchaRequest {
  email: string;
}

// 发送验证码响应
export interface SendCaptchaResponse {
  code: number;
  msg: string;
  success: boolean;
  timestamp: number;
  data: boolean;
}

// 忘记密码请求参数（password 需与注册一致使用 RSA 加密）
export interface ForgotPasswordRequest {
  email: string;
  password: string;
  captcha: string;
}

// 忘记密码响应
export interface ForgotPasswordResponse {
  code: number;
  msg: string;
  success: boolean;
  timestamp: number;
  data: null;
}

// Google OAuth 响应
export interface GoogleOAuthResponse {
  code: number;
  msg: string;
  success: boolean;
  timestamp: number;
  data: {
    authorizeUrl: string;
  };
}

// OAuth2 授权响应
export interface OAuth2AuthorizeResponse {
  code: number;
  msg: string;
  success: boolean;
  timestamp: number;
  data: {
    code: string;
  } | null;
}

// OAuth2 令牌颁发请求参数
export interface OAuth2TokenRequest {
  grant_type: "authorization_code";
  code: string;
  client_id: string;
  redirect_uri?: string;
}

// OAuth2 令牌颁发响应
export interface OAuth2TokenResponse {
  code: number;
  msg: string;
  success: boolean;
  timestamp: number;
  data: {
    access_token: string;
    token_type: string;
    expires_in: number;
    refresh_token?: string;
    scope?: string;
  } | null;
}

/**
 * 登录接口
 */
export async function login(params: LoginRequest): Promise<LoginResponse> {
  try {
    const data = await authApiClient.post<{ token: string }>("/auth/login", params, {
      needAuth: false, // 登录不需要认证
    });

    // 检查业务逻辑是否成功
    if (!data.success || !data.data) {
      const errorMsg = data.msg || "登录失败";
      logger.error("Login failed", { email: params.email, msg: errorMsg, code: data.code });
      throw new Error(errorMsg);
    }

    logger.info("Login successful", { email: params.email });
    return {
      ...data,
      data: data.data,
    } as LoginResponse;
  } catch (error) {
    logger.error("Login failed", error as Error);
    throw error;
  }
}

/**
 * 注册接口
 */
export async function register(params: RegisterRequest): Promise<RegisterResponse> {
  try {
    const data = await authApiClient.post<RegisterResult>("/api/register/email", params, {
      needAuth: false, // 注册不需要认证
    });

    logger.info("Register successful", { email: params.email });
    return {
      ...data,
      data: data.data ?? null,
    } as RegisterResponse;
  } catch (error) {
    logger.error("Register failed", error as Error);
    throw error;
  }
}

/**
 * 注册邀请码校验接口
 * 仅用于注册页体验提示，最终归因和奖励以后端注册响应为准。
 */
export async function validateRegisterInviteCode(
  inviteCode: string
): Promise<RegisterInviteCodeValidateResponse> {
  try {
    const queryParams = new URLSearchParams({
      inviteCode: inviteCode.trim().slice(0, 64),
    });

    const data = await authApiClient.get<RegisterInviteCodeValidateResult>(
      `/api/register/invite-codes/validate?${queryParams.toString()}`,
      {
        needAuth: false,
      }
    );

    if (!data.success) {
      const errorMsg = data.msg || "邀请码校验失败";
      logger.error("Validate register invite code failed", { msg: errorMsg, code: data.code });
      throw new Error(errorMsg);
    }

    return {
      ...data,
      data: data.data ?? null,
    } as RegisterInviteCodeValidateResponse;
  } catch (error) {
    logger.error("Validate register invite code failed", error as Error);
    throw error;
  }
}

/**
 * 获取用户信息接口
 * 注意：token 通过 Authorization 请求头传递（Bearer token）
 */
export async function getUserInfo(token?: string): Promise<UserInfoResponse> {
  try {
    const data = await authApiClient.get<UserInfo>("/auth/user/info", {
      needAuth: true,
    });

    // 检查业务逻辑是否成功
    if (!data.success || !data.data) {
      const errorMsg = data.msg || "获取用户信息失败";
      logger.error("Get user info failed", { msg: errorMsg, code: data.code });
      throw new Error(errorMsg);
    }

    logger.info("Get user info successful", { userId: data.data.id });
    return {
      ...data,
      data: data.data,
    } as UserInfoResponse;
  } catch (error) {
    logger.error("Get user info failed", error as Error);
    throw error;
  }
}

/**
 * 修改当前用户的昵称和头像。
 * 请求使用 multipart/form-data，未传字段保持原值。
 */
export async function updateUserProfile(
  params: UpdateUserProfileRequest,
): Promise<UpdateUserProfileResult> {
  try {
    const formData = new FormData();
    if (params.nickname !== undefined) {
      formData.append("nickname", params.nickname);
    }
    if (params.avatar) {
      formData.append("avatar", params.avatar, params.avatar.name || "avatar.webp");
    }

    const response = await authApiClient.patch<UpdateUserProfileResult>(
      "/auth/user/info",
      formData,
      { needAuth: true },
    );
    const result = handleApiResponse(response, "个人资料修改失败");

    logger.info("Update user profile successful", {
      nicknameUpdated: params.nickname !== undefined,
      avatarUpdated: Boolean(params.avatar),
    });
    return result;
  } catch (error) {
    logger.error("Update user profile failed", error as Error);
    throw error;
  }
}

/**
 * 忘记密码接口（POST /auth/forgot-password，password 需 RSA 加密）
 */
export async function forgotPassword(
  params: ForgotPasswordRequest
): Promise<ForgotPasswordResponse> {
  try {
    const data = await authApiClient.post<null>("/auth/forgot-password", params, {
      needAuth: false,
    });
    logger.info("Forgot password request successful", { email: params.email });
    return {
      ...data,
      data: null,
    } as ForgotPasswordResponse;
  } catch (error) {
    logger.error("Forgot password failed", error as Error);
    throw error;
  }
}

/**
 * 发送验证码接口
 */
export async function sendCaptcha(params: SendCaptchaRequest): Promise<SendCaptchaResponse> {
  try {
    const queryParams = new URLSearchParams({
      email: params.email,
    });

    const data = await authApiClient.get<boolean>(`/api/captcha/mail?${queryParams.toString()}`, {
      needAuth: false, // 发送验证码不需要认证
    });

    logger.info("Send captcha successful", { email: params.email });
    return {
      ...data,
      data: data.data ?? false,
    } as SendCaptchaResponse;
  } catch (error) {
    logger.error("Send captcha failed", error as Error);
    throw error;
  }
}

/**
 * Google OAuth 回调登录接口（POST /auth/login，用 code/state 换 token）
 */
export async function loginWithGoogleCallback(
  params: GoogleOAuthCallbackLoginRequest
): Promise<LoginResponse> {
  try {
    const data = await authApiClient.post<{ token: string }>("/auth/login", params, {
      needAuth: false,
    });

    // 检查业务逻辑是否成功
    if (!data.success || !data.data) {
      const errorMsg = data.msg || "登录失败";
      logger.error("Google OAuth callback login failed", { msg: errorMsg, code: data.code });
      throw new Error(errorMsg);
    }

    logger.info("Google OAuth callback login successful");
    return {
      ...data,
      data: data.data,
    } as LoginResponse;
  } catch (error) {
    logger.error("Google OAuth callback login failed", error as Error);
    throw error;
  }
}

/**
 * Google OAuth 登录接口（GET /auth/google，返回跳转地址）
 */
export async function getGoogleOAuthUrl(): Promise<GoogleOAuthResponse> {
  try {
    const data = await authApiClient.get<{ authorizeUrl: string }>("/auth/google", {
      needAuth: false, // 获取 OAuth URL 不需要认证
    });

    // 检查业务逻辑是否成功
    if (!data.success || !data.data) {
      const errorMsg = data.msg || "获取 Google OAuth URL 失败";
      logger.error("Get Google OAuth URL failed", { msg: errorMsg, code: data.code });
      throw new Error(errorMsg);
    }

    logger.info("Get Google OAuth URL successful");
    return {
      ...data,
      data: data.data,
    } as GoogleOAuthResponse;
  } catch (error) {
    logger.error("Get Google OAuth URL failed", error as Error);
    throw error;
  }
}

/**
 * OAuth2 授权接口
 * 已登录用户获取 authorization code，用于 SSO 跳转至子系统（如 toolbox、prompt）
 * 注意：token 通过 Authorization 请求头传递（Bearer token）
 *
 * @param clientId 子应用的客户端标识，如 "portal-a"（toolbox）、"portal-prompt"（prompt）
 */
export async function oauth2Authorize(clientId: string): Promise<OAuth2AuthorizeResponse> {
  try {
    const data = await authApiClient.get<{ code: string } | null>(
      `/oauth2/authorize?clientId=${encodeURIComponent(clientId)}`,
      {
        needAuth: true,
      }
    );

    logger.info("OAuth2 authorize successful", { clientId });
    return {
      ...data,
      data: data.data ?? null,
    } as OAuth2AuthorizeResponse;
  } catch (error) {
    logger.error("OAuth2 authorize failed", error as Error);
    throw error;
  }
}

